import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './ItemList.css';

/**
 * Generic ItemList component for displaying either movies or books
 * @param {Object} props - Component props
 * @param {string} props.type - 'movie' or 'book'
 * @param {Array} props.items - Array of items to display
 * @param {Function} props.fetchItems - Function to fetch items with pagination
 * @param {Object} props.preloadedItems - Preloaded items from context
 * @param {Object} props.activeFilters - Current active filters
 * @param {Function} props.renderFilterSection - Function to render filter section
 * @param {string} props.title - Page title
 * @param {Function} props.formatItemForDisplay - Function to format each item for display
 */
function ItemList({
  type,
  fetchItems,
  preloadedItems = [], 
  activeFilters = {},
  filterOptions = {},
  renderFilterSection,
  title = 'Items',
  formatItemForDisplay,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const loadingRef = useRef(null);
  const navigate = useNavigate();
  
  // Initialize sidebar state from localStorage with device-specific defaults
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    // Check if we're on mobile or desktop
    const isMobile = window.innerWidth <= 992;
    
    // If on desktop, always start with sidebar expanded regardless of localStorage
    if (!isMobile) {
      return true;
    }
    
    // On mobile, use the saved state or default to collapsed
    const savedState = localStorage.getItem(`${type}SidebarExpanded`);
    return savedState ? JSON.parse(savedState) : false;
  });
  
  // Track whether we're on a mobile device
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 992);
  
  const sidebarRef = useRef(null);
  const toggleButtonRef = useRef(null);

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarExpanded', JSON.stringify(sidebarExpanded));
    localStorage.setItem(`${type}SidebarExpanded`, JSON.stringify(sidebarExpanded));
  }, [sidebarExpanded, type]);

  // Handle window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 992;
      setIsMobile(mobile);
      
      // If transitioning to desktop, always expand the sidebar
      if (!mobile && !sidebarExpanded) {
        setSidebarExpanded(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarExpanded]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(`${type}ActiveFilters`, JSON.stringify(activeFilters));
  }, [activeFilters, type]);

  useEffect(() => {
    // Use preloaded data if available and no filters are active
    const noActiveFilters = Object.values(activeFilters).every(value => value === '');
    if (preloadedItems.length > 0 && noActiveFilters) {
      setItems(preloadedItems);
      setLoading(false);
      return;
    }

    const loadInitialItems = async () => {
      try {
        setLoading(true);
        const data = await fetchItems(1, activeFilters);
        
        // Support both data structures (movies and books APIs)
        if (data && data.results && data.results.length > 0) {
          setItems(data.results);
          setHasMore(data.page < data.total_pages);
        } else if (data && data.docs && data.docs.length > 0) {
          setItems(data.docs);
          setHasMore(data.hasMore);
        } else if (data && Array.isArray(data) && data.length > 0) {
          setItems(data);
          setHasMore(true);
        } else if ((data && data.results && data.results.length === 0) || (data && data.docs && data.docs.length === 0)) {
          setItems([]);
          setHasMore(false);
        }
        setError(null);
      } catch (err) {
        console.error(`Error fetching ${type}s:`, err);
        setError(`Failed to fetch ${type}s. The system is automatically retrying. Please wait or try again later.`);
      } finally {
        setLoading(false);
      }
    };

    loadInitialItems();
  }, [activeFilters, preloadedItems, fetchItems, type]);

  const loadMoreItems = useCallback(async (pageNumber) => {
    try {
      setLoading(true);
      const data = await fetchItems(pageNumber, activeFilters);
      
      // Support both data structures (movies and books APIs)
      if (pageNumber === 1) {
        if (data && data.results && data.results.length > 0) {
          setItems(data.results);
          setHasMore(data.page < data.total_pages);
        } else if (data && data.docs && data.docs.length > 0) {
          setItems(data.docs);
          setHasMore(data.hasMore);
        } else if (data && Array.isArray(data) && data.length > 0) {
          setItems(data);
          setHasMore(true);
        }
      } else {
        if (data && data.results && data.results.length > 0) {
          setItems(prevItems => [...prevItems, ...data.results]);
          setHasMore(data.page < data.total_pages);
        } else if (data && data.docs && data.docs.length > 0) {
          setItems(prevItems => [...prevItems, ...data.docs]);
          setHasMore(data.hasMore);
        } else if (data && Array.isArray(data) && data.length > 0) {
          setItems(prevItems => [...prevItems, ...data]);
          setHasMore(true);
        }
      }
      setError(null);
      return data;
    } catch (err) {
      console.error(`Error fetching more ${type}s:`, err);
      setError(`Failed to fetch more ${type}s. The system is automatically retrying. Please wait or try again later.`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [activeFilters, fetchItems, type]);

  const lastItemElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    }, { threshold: 1.0 });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  useEffect(() => {
    if (page > 1) {
      setLoading(true);
      loadMoreItems(page).finally(() => setLoading(false));
    }
  }, [page, loadMoreItems]);

  // Handle click outside to close sidebar on mobile
  useEffect(() => {
    function handleClickOutside(event) {
      // Only apply this behavior on mobile (window width < 993px)
      if (window.innerWidth < 993) {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target) && 
            !event.target.closest('.toggle-sidebar')) {
          setSidebarExpanded(false);
        }
      }
    }
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const hasActiveFilters = Object.values(activeFilters).some(value => value !== '');

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className={`item-list-container ${type}-list-container ${sidebarExpanded ? 'sidebar-expanded' : ''}`}>
      {/* Only show toggle button on mobile and when sidebar is closed */}
      {isMobile && !sidebarExpanded && (
        <button
          ref={toggleButtonRef}
          className="toggle-sidebar"
          onClick={() => setSidebarExpanded(true)}
        >
          <div className="filter-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 5a1 1 0 1 0 0 2 1 1 0 0 0 0-2zM6.17 5a3.001 3.001 0 0 1 5.66 0H19a1 1 0 1 1 0 2h-7.17a3.001 3.001 0 0 1-5.66 0H5a1 1 0 0 1 0-2h1.17zM15 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-2.83 0a3.001 3.001 0 0 1 5.66 0H19a1 1 0 1 1 0 2h-1.17a3.001 3.001 0 0 1-5.66 0H5a1 1 0 1 1 0-2h7.17zM9 17a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-2.83 0a3.001 3.001 0 0 1 5.66 0H19a1 1 0 1 1 0 2h-7.17a3.001 3.001 0 0 1-5.66 0H5a1 1 0 1 1 0-2h1.17z"/>
            </svg>
          </div>
          {hasActiveFilters && <span className="filter-badge"></span>}
        </button>
      )}

      <aside ref={sidebarRef} className={`sidebar ${sidebarExpanded ? 'expanded' : ''}`}>
        {renderFilterSection && renderFilterSection()}
      </aside>

      <main className="main-content">
        <h1>{title}</h1>
        {error && <div className="error">{error}</div>}
        {loading && items.length === 0 ? (
          <div className="loading">Loading {type}s... <span className="loading-message">(Retrying automatically if needed)</span></div>
        ) : items.length === 0 && !loading && !error ? (
          <div className="no-results">No {type}s found matching your criteria. Try adjusting your filters.</div>
        ) : (
          <div className={`items-grid ${type}s-grid`}>
            {items.map((item, index) => {
              // Choose component type based on mobile/sidebar state
              // Use a div on mobile with expanded sidebar, otherwise use Link
              const CardComponent = (isMobile && sidebarExpanded) ? 'div' : Link;
              
              // Format and render the item through the provided formatter
              const formattedItem = formatItemForDisplay(item);
              
              // Extract id for this item
              const itemId = formattedItem.id;
              
              // Prepare state data that will be passed regardless of navigation method
              const stateData = {
                title: formattedItem.title,
                // For books, include publication date/year
                ...(type === 'book' && {
                  first_publish_date: formattedItem.year,
                  authors: formattedItem.authors
                }),
                // For movies, include any relevant metadata
                ...(type === 'movie' && {
                  release_date: formattedItem.year
                })
              };
              
              // Store state data in localStorage as a fallback mechanism
              // This ensures the data is available even if React Router state is lost
              if (isMobile) {
                try {
                  // Clean up the ID to ensure consistent format
                  let storageId = itemId;
                  // Remove any leading slashes for consistency
                  if (storageId.startsWith('/')) {
                    storageId = storageId.substring(1);
                  }
                  
                  // Extract just the ID part without the "works/" prefix for consistency
                  // For example, convert "works/OL12345W" to just "OL12345W"
                  const idPart = storageId.includes('works/') 
                    ? storageId.replace('works/', '') 
                    : storageId;
                  
                  // Use the item ID as the key to ensure uniqueness
                  const storageKey = `${type}_${idPart}_data`;
                  localStorage.setItem(storageKey, JSON.stringify(stateData));
                } catch (error) {
                  console.error('Failed to store data in localStorage:', error);
                }
              }
              
              // Modify the handleCardClick function to pass state data
              const handleCardClick = (e) => {
                if (sidebarExpanded) {
                  e.preventDefault();
                  setSidebarExpanded(false);
                  
                  // Only navigate if we're on desktop
                  if (!isMobile) {
                    // Navigate after sidebar is closed
                    setTimeout(() => {
                      navigate(`/${type}/${itemId}`, { state: stateData });
                    }, 10);
                  }
                }
              };
              
              // Modify the onClick handlers for mobile
              const cardProps = (isMobile && sidebarExpanded) 
                ? {
                    className: `${type}-card`,
                    onClick: () => {
                      // First close the sidebar
                      setSidebarExpanded(false);
                      
                      // Add navigation for mobile when sidebar is expanded
                      setTimeout(() => {
                        // Navigate using the state data
                        navigate(`/${type}/${itemId}`, { state: stateData });
                      }, 300); // Delay to ensure sidebar animation completes
                    },
                    ref: index === items.length - 1 ? lastItemElementRef : null
                  }
                : {
                    to: {
                      pathname: `/${type}/${itemId}`,
                      // Pass important metadata as state to be available in the detail component
                      state: stateData
                    },
                    className: `${type}-card`,
                    onClick: (e) => {
                      // For mobile devices with collapsed sidebar or desktop with expanded sidebar
                      if (sidebarExpanded) {
                        handleCardClick(e);
                      }
                    },
                    ref: index === items.length - 1 ? lastItemElementRef : null
                  };
                
              return (
                <CardComponent 
                  key={formattedItem.id || index}
                  {...cardProps}
                >
                  {formattedItem.imageComponent}
                  {formattedItem.contentComponent}
                </CardComponent>
              );
            })}
          </div>
        )}
        {loading && items.length > 0 && (
          <div className="loading-more" ref={loadingRef}>Loading more {type}s...</div>
        )}
      </main>
    </div>
  );
}

export default ItemList; 